import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MicActivityWave = ({
  audioStream = null,
  barCount = 20,
  barWidth = 4,
  barGap = 4,
  height = 48,
  minBarHeight = 4,
  maxBarHeight = 32,
  smoothing = 0.75,
  requestMicIfNoStream = true,
}) => {
  const { t } = useTranslation('interview_container');

  const barsRef = useRef([]);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const ownStreamRef = useRef(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);

  useEffect(() => {
    let mounted = true;
    let prevLevels = new Float32Array(barCount).fill(minBarHeight);

    async function setup(stream) {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioContextRef.current = audioCtx;

        const source = audioCtx.createMediaStreamSource(stream);
        sourceRef.current = source;

        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.3;
        analyserRef.current = analyser;

        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const freqData = new Uint8Array(bufferLength);

        const animate = () => {
          if (!mounted) return;

          analyser.getByteFrequencyData(freqData);
          const binsPerBar = Math.floor(bufferLength / barCount) || 1;

          for (let i = 0; i < barCount; i++) {
            let sum = 0;
            for (let j = 0; j < binsPerBar; j++) {
              const idx = i * binsPerBar + j;
              if (idx < bufferLength) sum += freqData[idx];
            }

            const avg = sum / binsPerBar;
            const normalized = avg / 255;
            const targetHeight = Math.max(
              minBarHeight,
              Math.min(maxBarHeight, normalized * maxBarHeight),
            );

            const smoothed = prevLevels[i] * smoothing + targetHeight * (1 - smoothing);
            prevLevels[i] = smoothed;

            const el = barsRef.current[i];
            if (el) el.style.height = `${Math.round(smoothed)}px`;
          }

          animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);
      } catch (err) {
        console.error('MicActivityWave setup error:', err);
        setHasPermissionError(true);
      }
    }

    (async () => {
      if (audioStream) {
        await setup(audioStream);
      } else if (requestMicIfNoStream) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          ownStreamRef.current = stream;
          await setup(stream);
        } catch (err) {
          console.error('Microphone access denied:', err);
          setHasPermissionError(true);
        }
      }
    })();

    return () => {
      mounted = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (analyserRef.current) analyserRef.current.disconnect();
      if (sourceRef.current) sourceRef.current.disconnect();
      if (audioContextRef.current) audioContextRef.current.close();
      if (ownStreamRef.current) ownStreamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [audioStream, barCount, maxBarHeight, minBarHeight, smoothing, requestMicIfNoStream]);

  const wrapperStyle = {
    display: 'inline-block',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
  };

  const visualStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: `${barGap}px`,
    height: `${height}px`,
    minWidth: `${barCount * (barWidth + barGap)}px`,
  };

  const barStyle = {
    width: `${barWidth}px`,
    background: 'linear-gradient(180deg, #7dd3fc 0%, #38bdf8 100%)',
    borderRadius: `${barWidth}px`,
    transition: 'height 80ms cubic-bezier(0.4, 0, 0.2, 1)',
    willChange: 'height',
    height: `${minBarHeight}px`,
  };

  return (
    <div style={wrapperStyle}>
      <div style={visualStyle}>
        {hasPermissionError ? (
          <div style={{ color: '#ef4444', fontSize: 12 }}>
            {t('mic_activity.microphone_blocked')}
          </div>
        ) : (
          Array.from({ length: barCount }).map((_, i) => (
            <div
              key={i}
              ref={el => (barsRef.current[i] = el)}
              style={barStyle}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MicActivityWave;
